/**
 * PasswordRequirements — real-time policy checklist shown under a new-password
 * field (F02 registration, F07 change-password). Consumes the shared
 * `PASSWORD_REQUIREMENTS` single source of truth from `engine/passwordValidation`
 * so the copy and rules always match the backend policy and the validator tests.
 *
 * Color semantics are strict (see .impeccable.md): a MET item uses the `ok`
 * token (green = ok/approve is the only sanctioned use). An UNMET item is
 * NEUTRAL `fg3`, NEVER red — red/danger is reserved for an actual submit error.
 * This is a checklist of ticks by design, not a fuzzy strength bar.
 *
 * Presentational + Tailwind utility classes (matches PasswordPanel/LoginScreen,
 * the codebase's stated styling direction). Renders no form controls, so it can
 * never add a stray `input[type="password"]` that the register selectors count.
 */

import { checkPassword, PASSWORD_REQUIREMENTS } from "../engine/passwordValidation";

export default function PasswordRequirements({ password = "", testId = "password-requirements" }) {
  const result = checkPassword(password);

  return (
    <ul data-testid={testId} aria-label="Password requirements" className="mt-2 space-y-1 list-none pl-0">
      {PASSWORD_REQUIREMENTS.map((req) => {
        const met = req.met(result);
        return (
          <li
            key={req.key}
            data-testid={`pw-req-${req.key}`}
            data-met={met ? "true" : "false"}
            className={`flex items-center gap-2 text-xs ${met ? "text-ok" : "text-fg3"}`}
          >
            <span aria-hidden="true" className="inline-block w-3 text-center">
              {met ? "✓" : "○"}
            </span>
            <span>{req.label}</span>
          </li>
        );
      })}
    </ul>
  );
}
